import React, { useEffect, useState } from 'react';
import { networkManager } from '../../systems/NetworkManager';
import { onGameEvent } from '../../core/GameEvents';

const PALETTE = {
  DARK: '#0f1b26',
  PANEL: '#1a2a38',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  DIM: '#4a5a6a',
};

const FONT_DATA = "'JetBrains Mono', monospace";
const PANEL_WIDTH = 460;

const WELCOME_TITLE = 'Welcome to Live Space';
const WELCOME_BODY = `This constellation is one of many. They stretch nine hundred kilometers across — eighteen times the Academy — and they are never empty. Hauler drones run cargo between stations. Miners work the belts. Fighters patrol safe zones, rogues hunt outside them. Solar systems orbit suns. Planets wait to be settled. Markets shift every minute on what other pilots are buying and selling.

Two truths to remember: space is dangerous, and every choice has a price. Reputation matters. Death matters. The pilots who last learn fast.

Aura, signing off. Good wondering.`;

/**
 * One-shot post-tutorial Aura transmission. Fires when the server pushes
 * `welcomeToLiveSpace` (first non-tutorial constellation join after the Academy is
 * done). Dismiss-on-Close flips the server flag so it never reappears for
 * this player across sessions.
 *
 * Visual styling intentionally mirrors TutorialTransmissionPanel — same
 * palette, frame, header chip — so the player reads it as "the last Aura
 * message" rather than a separate UI.
 */
export function WelcomeTransmissionPanel(): React.ReactElement | null {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const unsub = onGameEvent('welcomeToLiveSpace', () => setOpen(true));
    return () => { unsub(); };
  }, []);

  if (!open) return null;

  const dismiss = () => {
    setOpen(false);
    networkManager.sendWelcomeToLiveSpaceAck();
  };

  const x = Math.max(20, window.innerWidth / 2 - PANEL_WIDTH / 2);

  return (
    <div
      style={{
        position: 'fixed',
        left: x,
        top: 90,
        width: PANEL_WIDTH,
        backgroundColor: PALETTE.DARK,
        border: `1px solid ${PALETTE.TEAL}88`,
        borderRadius: '4px',
        boxShadow: `0 0 25px ${PALETTE.TEAL}33, 0 4px 30px rgba(0,0,0,0.6)`,
        fontFamily: FONT_DATA,
        zIndex: 1000,
        userSelect: 'none',
        pointerEvents: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${PALETTE.PANEL}`,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '11px',
          color: PALETTE.TEAL,
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: PALETTE.TEAL,
            boxShadow: `0 0 6px ${PALETTE.TEAL}`,
          }}
        />
        Transmission
      </div>

      {/* Body */}
      <div style={{ padding: '16px 18px 14px' }}>
        <div style={{ color: PALETTE.LIGHT, fontSize: '12px', marginBottom: '4px' }}>Aura</div>
        <div
          style={{
            fontSize: '13px',
            fontWeight: 700,
            color: PALETTE.TEAL,
            marginBottom: '14px',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
          }}
        >
          {WELCOME_TITLE}
        </div>
        <div
          style={{
            color: PALETTE.LIGHT,
            fontSize: '12px',
            lineHeight: 1.55,
            whiteSpace: 'pre-line',
          }}
        >
          {WELCOME_BODY}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${PALETTE.PANEL}`,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={dismiss}
          style={{
            backgroundColor: PALETTE.TEAL,
            color: PALETTE.DARK,
            border: 'none',
            padding: '8px 22px',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '1.2px',
            cursor: 'pointer',
            fontFamily: FONT_DATA,
            textTransform: 'uppercase',
            borderRadius: '2px',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
